import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { addClient } from '../services/clientService';
import { uploadFiles, deleteFile, getSignedUrl } from '../services/fileService';
import { useNavigate, Link } from 'react-router-dom';
import { terminology } from '../config/terminology'; // Import terminology
import { FaFileUpload, FaPaperclip, FaTimes, FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel } from 'react-icons/fa';

const AddClientPage = () => {
  const { register, handleSubmit, setValue, control, formState: { errors, isSubmitting } } = useForm({
    mode: 'onBlur',
    defaultValues: {
        clientName: '',
        email: '',
        phoneNumber: '',
        firstName: '',
        lastName: '',
        caseManagerFirstName: '',
        caseManagerLastName: '',
        caseManagerEmail: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        postalCode: '',
        country: 'Australia', // Add default value for country
        xeroContactId: '', // Added xeroContactId
        poNumber: '',
        agedCare: false, // Aged Care checkbox
        hourlyRate: '', // Hourly rate
        durationInIncrements: false, // Duration in 15 min increments
    }
  });
  const navigate = useNavigate();
  const [apiError, setApiError] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, file: null });
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // File handling functions
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <FaFilePdf className="text-red-500" />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return <FaFileImage className="text-blue-500" />;
      case 'doc': case 'docx': return <FaFileWord className="text-blue-600" />;
      case 'xls': case 'xlsx': return <FaFileExcel className="text-green-600" />;
      default: return <FaFile className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert('File size must be less than 10MB');
      return;
    }

    // Add file to attached files list with empty category (to be filled by user)
    const newFile = {
      id: Date.now() + Math.random(), // Simple unique ID
      file: file,
      category: '', // Empty initially - user will fill this in
      name: file.name,
      size: file.size
    };

    setAttachedFiles(prev => [...prev, newFile]);
    
    // Clear the input
    event.target.value = '';
  };

  const updateFileCategory = (fileId, category) => {
    setAttachedFiles(prev => 
      prev.map(file => 
        file.id === fileId ? { ...file, category: category.trim() } : file
      )
    );
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleViewFile = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const removeUploadedFile = (fileId) => {
    const fileToDelete = uploadedFiles.find(f => f.id === fileId);
    if (fileToDelete) {
      setDeleteConfirmModal({ show: true, file: fileToDelete });
    }
  };

  const confirmDeleteFile = async () => {
    const fileToDelete = deleteConfirmModal.file;
    if (!fileToDelete) return;

    setDeleting(true);
    try {
      // Call the delete API endpoint
      await deleteFile(fileToDelete.url);
      
      // Remove the file from the uploaded files list
      setUploadedFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      
      // Close the modal
      setDeleteConfirmModal({ show: false, file: null });
      
      // Show success message
      alert('File deleted successfully!');
    } catch (error) {
      console.error('File deletion error:', error);
      alert(error.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteConfirmModal({ show: false, file: null });
  };

  const handleUploadFiles = async () => {
    if (attachedFiles.length === 0) {
      alert('No files to upload');
      return;
    }

    console.log('attachedFiles before upload:', attachedFiles);

    // Check if all files have categories
    const filesWithoutCategory = attachedFiles.filter(file => !file.category || file.category.trim() === '');
    if (filesWithoutCategory.length > 0) {
      alert('Please enter a category for all files before uploading');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const response = await uploadFiles(attachedFiles);
      console.log('Upload successful:', response);
      
      // Move uploaded files to uploadedFiles array with URLs
      const filesWithUrls = attachedFiles.map((file, index) => ({
        ...file,
        url: response.data?.urls?.[index] || null,
        uploaded: true
      }));
      
      setUploadedFiles(prev => [...prev, ...filesWithUrls]);
      
      // Clear attached files after successful upload
      setAttachedFiles([]);
      
      // Show success message
      alert(`${response.data?.urls?.length || attachedFiles.length} file(s) uploaded successfully!`);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data) => {
    setApiError(null);
    try {
      // Prepare the payload
      const payload = {
        ...data
      };

      // Include attachments if there are uploaded files
      if (uploadedFiles.length > 0) {
        payload.attachments = uploadedFiles.map(file => ({
          type: file.category,
          url: file.url
        }));
      }

      await addClient(payload);
      navigate('/clients', { state: { message: `${terminology.client.singular} added successfully!` } }); // Use terminology
    } catch (error) {
      setApiError(error.message || `Failed to add ${terminology.client.singular.toLowerCase()}. Please try again.`); // Use terminology
      console.error("Submission error:", error);
      
      // If client creation failed but files were uploaded, clean them up
      if (uploadedFiles.length > 0) {
        console.log('Client creation failed, cleaning up uploaded files...');
        try {
          await Promise.all(uploadedFiles.map(file => deleteFile(file.url)));
          console.log('Uploaded files cleaned up successfully');
          setUploadedFiles([]);
        } catch (cleanupError) {
          console.error('Failed to clean up uploaded files:', cleanupError);
        }
      }
    }
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-jims-cyan-dark">Add New {terminology.client.singular}</h1> {/* Use terminology */}
        <Link to="/clients" className="text-jims-cyan-dark hover:text-jims-cyan underline">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}

        {/* Supplier Main Details */}
        <div className = "border p-6 border-gray-300 bg-gray-50 rounded-lg space-y-6">
          <div>
            <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">{terminology.client.singular} Name *</label> {/* Use terminology */}
            <input
              id="clientName"
              type="text"
              {...register("clientName", { required: `${terminology.client.singular} Name is required.` })} /* Use terminology */
              className={`mt-1 block w-full px-3 py-2 border ${errors.clientName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.clientName && <p className="mt-1 text-xs text-red-600">{errors.clientName.message}</p>}
          </div>
          <div class="flex w-full gap-6">
            <div className="flex-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                id="email"
                type="email"
                {...register("email", {
                  required: "Email is required.",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address."
                  }
                })}
                className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div className="flex-1">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number *</label>
              <input
                id="phoneNumber"
                type="tel"
                {...register("phoneNumber", { required: "Phone Number is required." })}
                className={`mt-1 block w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
              />
              {errors.phoneNumber && <p className="mt-1 text-xs text-red-600">{errors.phoneNumber.message}</p>}
            </div>
          </div>
          
          {/* Case Manager Contact Details */}
          <div className="border-t pt-6 mt-6 border-gray-200">
              <p className="mb-3 block text-sm font-medium text-gray-700">Case Manager Contact Details:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label htmlFor="caseManagerFirstName" className="block text-xs font-medium text-gray-700">First Name</label>
                      <input
                          id="caseManagerFirstName"
                          type="text"
                          {...register("caseManagerFirstName")}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                      />
                  </div>
                  <div>
                      <label htmlFor="caseManagerLastName" className="block text-xs font-medium text-gray-700">Last Name</label>
                      <input
                          id="caseManagerLastName"
                          type="text"
                          {...register("caseManagerLastName")}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                      />
                  </div>
              </div>
              <div className="mt-6">
                  <label htmlFor="caseManagerEmail" className="block text-xs font-medium text-gray-700">Email</label>
                  <input
                      id="caseManagerEmail"
                      type="email"
                      {...register("caseManagerEmail", {
                          pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address."
                          }
                      })}
                      className={`mt-1 block w-full px-3 py-2 border ${errors.caseManagerEmail ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                  />
                  {errors.caseManagerEmail && <p className="mt-1 text-xs text-red-600">{errors.caseManagerEmail.message}</p>}
              </div>
          </div>
        </div>

        {/* PO Number, Aged Care and Hourly Rate Fields */}
        <div className="space-y-6">
          <div>
            <label htmlFor="poNumber" className="block text-sm font-medium text-gray-700">PO Number</label>
            <input
              id="poNumber"
              type="text"
              {...register("poNumber")}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
            />
          </div>
          <div className='flex w-full gap-6'>
            <div className="flex-1">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  id="agedCare"
                  type="checkbox"
                  {...register("agedCare")}
                  className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Aged Care</span>
              </label>
            </div>
            <div className="flex-1">
              <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700">Hourly Rate</label>
              <input
                id="hourlyRate"
                type="number"
                step="0.01"
                min="0"
                {...register("hourlyRate")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                placeholder="0.00"
              />
            </div>
            <div className="flex-1">
              <label className="flex items-center space-x-2 cursor-pointer mt-6">
                <input
                  id="durationInIncrements"
                  type="checkbox"
                  {...register("durationInIncrements")}
                  className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Duration in 15 min increments</span>
              </label>
            </div>
          </div>
        </div>

        {/* Contact Person Name Fields */}
        <div className="pt-6 mt-6 ">
            <p className="text-sm text-gray-600 mb-3">Contact Person Details:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
                    <input
                        id="firstName"
                        type="text"
                        {...register("firstName", { required: "First Name is required." })}
                        className={`mt-1 block w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                    />
                    {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
                </div>
                <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                        id="lastName"
                        type="text"
                        {...register("lastName")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
            </div>
        </div>

        {/* Xero Contact ID Field - ADDED */}
        <div>
            <label htmlFor="xeroContactId" className="block text-sm font-medium text-gray-700">Xero Contact ID</label>
            <input
                id="xeroContactId"
                type="text"
                {...register("xeroContactId")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
            />
        </div>

        {/* Optional Fields */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <p className="text-sm text-gray-600 mb-3">Optional Address Information:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <AddressAutocomplete
                    control={control}
                    setValue={setValue}
                 />
                <div>
                    <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700">Address Line 2</label>
                    <input
                        id="addressLine2"
                        type="text"
                        {...register("addressLine2")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
                    <input
                        id="state"
                        type="text"
                        {...register("state")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                        id="postalCode"
                        type="text"
                        {...register("postalCode")}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                    />
                </div>
                {/* Add Country Field */}
                <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                    <input
                        id="country"
                        type="text"
                        {...register("country")}
                        readOnly
                        value="Australia" // Explicitly set the value here
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm bg-gray-100 cursor-not-allowed"
                    />
                </div>
            </div>
        </div>

        {/* File Upload Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <p className="text-sm text-gray-600 mb-3">File Attachments:</p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="*/*"
          />

          {/* Attach File Link */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAttachFile}
              className="inline-flex items-center text-jims-cyan hover:text-jims-cyan-dark font-medium text-sm"
            >
              <FaPaperclip className="mr-2" />
              Attach File
            </button>
            <span className="ml-2 text-xs text-gray-500">(Max 10MB per file)</span>
          </div>

          {/* Display attached files (to be uploaded) */}
          {attachedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Files to Upload:</h4>
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Category *
                    </label>
                    <input
                      type="text"
                      value={file.category}
                      onChange={(e) => updateFileCategory(file.id, e.target.value)}
                      placeholder="e.g., contracts, documents, images"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                  </div>
                </div>
              ))}
              
              {/* Upload Files Button - right after files to upload */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white text-sm font-medium rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFileUpload className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${attachedFiles.length} File(s)`}
                </button>
              </div>
            </div>
          )}

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleViewFile(file.url)}
                          className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark underline truncate block text-left"
                          title="Click to download file"
                        >
                          {file.name}
                        </button>
                        <p className="text-xs text-gray-500">
                          {file.size > 0 ? formatFileSize(file.size) : 'Size unknown'} • Category: {file.category}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          ✓ Uploaded successfully
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md">
              {uploadError}
            </div>
          )}
        </div>

        {/* Confirmation Modal for File Deletion */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm File Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirmModal.file?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sticky Action Bar
          Variant A (active): full viewport width
          Variant B (swap): replace this div with:
            <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
                ...buttons...
              </div>
            </div>
        */}
        <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/clients" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : `Add ${terminology.client.singular}`} {/* Use terminology */}
          </button>
        </div>
        </div>
      </form>
    </div>
  );
};

export default AddClientPage;
